import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Login from "./components/auth/Login";
import AppRoutes from "./components/routing/Routes";
import ResponseInterceptor from "../src/utils/interceptor";
import { connect } from "react-redux";
import { LOGIN_SUCCESS, GET_TENANT_INFO, LOGOUT } from "./action/types";
import setAuthToken from "./utils/setAuthToken";
import { isAccessTokenStale, readStoredAuthInfo } from "./utils/sessionAuth";
import { refreshAccessToken } from "./utils/tokenRefresh";
import setTenantId from "./utils/setTenantId";
import "./App.css";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeProvider } from "@mui/material/styles";
import { updateDesignSystem } from './theme/designSystemStore';
import { muiTheme } from './theme/muiTheme';
import ResponsiveGlobalStyles from './theme/ResponsiveGlobalStyles';
import NavigationAlertReset from "./components/layout/NavigationAlertReset";

const App = ({ tenantInfo, dispatch }) => {
  const [appReady, setAppReady] = useState(false);

  useEffect(() => {
    ResponseInterceptor();

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const tenantStr = localStorage.getItem("tenantInfo");

        if (tenantStr) {
          const parsedTenant = JSON.parse(tenantStr);
          dispatch({
            type: GET_TENANT_INFO,
            payload: parsedTenant,
          });
          setTenantId(parsedTenant.tenantId);
        }

        const parsedAuth = readStoredAuthInfo();
        const sortCategories = (list = []) =>
          Array.from(
            new Set(
              (Array.isArray(list) ? list : [])
                .filter(Boolean)
                .map((item) => String(item))
            )
          ).sort((a, b) => a.localeCompare(b));

        if (parsedAuth?.accessToken) {
          const normalizedAuth = {
            ...parsedAuth,
            allowedDeviceCategories: sortCategories(
              parsedAuth.allowedDeviceCategories
            ),
          };
          localStorage.setItem("authInfo", JSON.stringify(normalizedAuth));
          dispatch({ type: LOGIN_SUCCESS, payload: normalizedAuth });
          setAuthToken(parsedAuth.accessToken);

          if (isAccessTokenStale(parsedAuth)) {
            const newToken = await refreshAccessToken();
            if (!newToken && !cancelled) {
              dispatch({ type: LOGOUT });
              setAuthToken();
            }
          }
        }
      } catch (error) {
        console.error("App bootstrap failed:", error);
      }

      if (!cancelled) {
        setAppReady(true);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (tenantInfo) {
      updateDesignSystem(tenantInfo);
    }
  }, [tenantInfo]);

  useEffect(() => {
    const favicon = document.getElementById("favicon");
    favicon.setAttribute("href", tenantInfo.tenantFaviconUrl);
    // favicon.href = logo;
  }, [tenantInfo.tenantFaviconUrl]);

  let className = "main-wrapper ";
  if (tenantInfo.primaryColor) {
    className +=
      tenantInfo.primaryColor +
      "-theme " +
      tenantInfo.secondaryColor +
      "-theme-secondary ";
  } else {
    className += "blue-theme lime-theme-secondary ";
  }

  if (
    tenantInfo.selectedTheme &&
    ["round", "rectangular"].includes(tenantInfo.selectedTheme.toLowerCase())
  ) {
    className += tenantInfo.selectedTheme.toLowerCase() + " roundOrRectangular";
  }

  if (!appReady) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size="5em" style={{ color: "#AFB42B" }} />
      </div>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <ResponsiveGlobalStyles />
      <BrowserRouter>
        <NavigationAlertReset />
        <section className={className}>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
        </section>
      </BrowserRouter>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    tenantInfo: state.tenant.tenantInfo,
  };
};

export default connect(mapStateToProps)(App);
